import { AxiosInstance } from 'axios';
import { Page, PageRevision, PageRevisionResult } from 'referral-types';
import { addFormValues, simulate } from './utils';
import { type FormFields, type PageNameFormFields } from '../components/page/page.schema';
import { SITE_URL } from './service';

export const baseEndpoint = 'page';

export class PageApi {
  private apiInstance;

  constructor(apiInstance: AxiosInstance) {
    this.apiInstance = apiInstance;
  }

  async getPage(pageId: string): Promise<Page> {
    await simulate();
    const response = await this.apiInstance.get(`/${pageId}`);

    return response.data;
  }

  async getPageByName(pageName: string): Promise<Page> {
    await simulate();
    const response = await this.apiInstance.get(`/name/${pageName}`);

    return response.data;
  }

  async isUsablePageName(pageName: string, signal?: AbortSignal): Promise<boolean> {
    await simulate();
    const response = await this.apiInstance.get(`/usable-name/${pageName}`, { signal });

    return response.data;
  }

  async updatePageName(pageId: string, formValues: PageNameFormFields): Promise<Page> {
    await simulate();
    const response = await this.apiInstance.put(`/name/${pageId}`, formValues);

    return response.data;
  }

  async createPage(): Promise<Page> {
    await simulate();
    const response = await this.apiInstance.post(`/`);

    return response.data;
  }

  // Admin only: orphan a user's current page and create a fresh one for them.
  // Returns the new page and the id of the now-orphaned page (claimable via token).
  async replacePageForUser(userId: string): Promise<{ orphanedPageId: string | null; page: Page }> {
    await simulate();
    const response = await this.apiInstance.post(`/admin/replace-for-user`, { userId });

    return response.data;
  }

  async getPageRevisions(pageId: string): Promise<PageRevisionResult> {
    await simulate();
    const response = await this.apiInstance.get(`${pageId}/revisions`);

    return response.data;
  }

  async getPageRevisionsByName(pageName: string): Promise<PageRevisionResult> {
    await simulate();
    const response = await this.apiInstance.get(`name/${pageName}/revisions`);

    return response.data;
  }

  async updatePage(
    pageId: string,
    updates: FormFields,
    files?: {
      logoFile?: File | null;
      backgroundFile?: File | null;
      backgroundMobileFile?: File | null;
      groupMediaFile?: File | null;
      headerMediaFile?: File | null;
    }
  ): Promise<PageRevision> {
    await simulate();
    const formData = new FormData();

    addFormValues(updates, formData);

    if (files?.logoFile || files?.logoFile === null) {
      formData.append('logoFile', files.logoFile ? files.logoFile : 'null');
    }
    if (files?.backgroundFile || files?.backgroundFile === null) {
      formData.append('backgroundFile', files.backgroundFile ? files.backgroundFile : 'null');
    }
    if (files?.backgroundMobileFile || files?.backgroundMobileFile === null) {
      formData.append('backgroundMobileFile', files.backgroundMobileFile ? files.backgroundMobileFile : 'null');
    }
    if (files?.groupMediaFile || files?.groupMediaFile === null) {
      formData.append('groupMediaFile', files.groupMediaFile ? files.groupMediaFile : 'null');
    }
    if (files?.headerMediaFile || files?.headerMediaFile === null) {
      formData.append('headerMediaFile', files.headerMediaFile ? files.headerMediaFile : 'null');
    }

    const response = await this.apiInstance.put(`/${pageId}`, formData);

    return response.data;
  }

  async publishPage(pageId: string): Promise<Page> {
    await simulate();
    const response = await this.apiInstance.post(`/${pageId}/publish`);

    return response.data;
  }

  getLogoImageSrc(pageId: string): string {
    return `${SITE_URL}/image/logo/${pageId}`;
  }

  getBackgroundImageSrc(pageId: string, isEditing: boolean, cacheBuster?: string): string {
    const params = new URLSearchParams();

    if (isEditing) {
      params.append('isEditing', 'true');
    }
    // if (isMobile) {
    //     params.append('isMobile', 'true');
    // }
    if (cacheBuster) {
      params.append('cachebuster', cacheBuster);
    }

    const queryString = params.toString();
    return `${SITE_URL}/image/background/${pageId}${queryString ? `?${queryString}` : ''}`;
  }

  getGroupMediaSrc(pageId: string, isEditing: boolean, cacheBuster?: string): string {
    const params = new URLSearchParams();

    if (isEditing) {
      params.append('isEditing', 'true');
    }
    if (cacheBuster) {
      params.append('cachebuster', cacheBuster);
    }

    const queryString = params.toString();
    return `${SITE_URL}/image/group-media/${pageId}${queryString ? `?${queryString}` : ''}`;
  }

  getHeaderMediaSrc(pageId: string, isEditing: boolean, cacheBuster?: string): string {
    const params = new URLSearchParams();

    if (isEditing) {
      params.append('isEditing', 'true');
    }
    if (cacheBuster) {
      params.append('cachebuster', cacheBuster);
    }

    const queryString = params.toString();
    return `${SITE_URL}/image/header-media/${pageId}${queryString ? `?${queryString}` : ''}`;
  }

  async getPublishedPages(limit: number = 10): Promise<Page[]> {
    await simulate();
    const response = await this.apiInstance.get(`/published?limit=${limit}`);

    return response.data;
  }
}
